import { useState } from 'react';
import { GOLD, GREEN, RED, MUTED, BORDER, TEXT, CARD_BG } from '../constants';
import { buildBackupBlob, triggerDownload, exportCSV, loadLS, saveLS, LS_KEYS } from '../utils';
import { SectionTitle, Card, FormField, Input, PrimaryBtn, GhostBtn } from './UI';

export default function Settings({ transactions, invoices, settings, onSettingsChange, lastBackup, setLastBackup }) {
  const [pw,       setPw]       = useState('');
  const [newPw,    setNewPw]    = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [pwMsg,    setPwMsg]    = useState(null);
  const [imported, setImported] = useState(null);

  function changePassword(e) {
    e.preventDefault();
    if (pw !== settings.password) { setPwMsg({ type: 'error', text: 'Current password incorrect' }); return; }
    if (newPw.length < 6)         { setPwMsg({ type: 'error', text: 'New password must be 6+ characters' }); return; }
    if (newPw !== confirm)        { setPwMsg({ type: 'error', text: 'Passwords do not match' }); return; }
    onSettingsChange({ ...settings, password: newPw });
    setPwMsg({ type: 'success', text: 'Password updated successfully' });
    setPw(''); setNewPw(''); setConfirm('');
  }

  function doBackupJSON() {
    const blob = buildBackupBlob(transactions, invoices, settings);
    const date = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    triggerDownload(blob, `AGC_Backup_${date}.json`);
    const ts = new Date().toLocaleString();
    setLastBackup(ts);
    saveLS(LS_KEYS.lastBackup, ts);
  }

  function doExportCSV() {
    const blob = exportCSV(transactions);
    triggerDownload(blob, `AGC_Transactions_${new Date().toISOString().slice(0,10)}.csv`);
  }

  function handleImport(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (data.transactions && Array.isArray(data.transactions)) {
          setImported({ transactions: data.transactions, invoices: data.invoices || [] });
        } else {
          alert('Invalid backup file format.');
        }
      } catch { alert('Could not parse backup file.'); }
    };
    reader.readAsText(file);
  }

  function confirmImport() {
    if (!imported) return;
    saveLS(LS_KEYS.transactions, imported.transactions);
    saveLS(LS_KEYS.invoices, imported.invoices);
    alert(`Imported ${imported.transactions.length} transactions and ${imported.invoices.length} invoices. Reload the page to see the data.`);
    setImported(null);
  }

  return (
    <div>
      <SectionTitle title="Settings & Backup" sub="Data management, security, and exports" />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

        {/* BACKUP */}
        <Card style={{ padding: 28 }}>
          <div style={{ fontSize: 12, color: GOLD, letterSpacing: 1, marginBottom: 20 }}>DATA BACKUP & EXPORT</div>
          <p style={{ color: MUTED, fontSize: 12, marginBottom: 20, lineHeight: 1.7 }}>
            All data is stored in your browser's localStorage. Export a JSON backup regularly and upload to Google Drive, Dropbox, or any cloud storage.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <PrimaryBtn onClick={doBackupJSON} style={{ width: '100%', padding: 12 }}>
              ↓ Download Full JSON Backup
            </PrimaryBtn>
            <GhostBtn onClick={doExportCSV} style={{ width: '100%', padding: 12 }}>
              ↓ Export Transactions CSV
            </GhostBtn>
          </div>
          {lastBackup && (
            <div style={{ color: GREEN, fontSize: 10, marginTop: 14, letterSpacing: 0.5 }}>
              ✓ Last backup: {lastBackup}
            </div>
          )}
          <div style={{ marginTop: 20, padding: '12px 16px', background: '#0D1117', borderRadius: 8, fontSize: 11, color: MUTED, lineHeight: 1.8 }}>
            <strong style={{ color: TEXT }}>Google Drive setup:</strong><br />
            1. Download JSON backup above<br />
            2. Go to drive.google.com<br />
            3. Create folder "AGC Backups"<br />
            4. Upload the file — done ✓<br />
            <span style={{ fontSize: 10, opacity: 0.7 }}>Set a calendar reminder to back up weekly.</span>
          </div>
        </Card>

        {/* RESTORE */}
        <Card style={{ padding: 28 }}>
          <div style={{ fontSize: 12, color: GOLD, letterSpacing: 1, marginBottom: 20 }}>RESTORE FROM BACKUP</div>
          <p style={{ color: MUTED, fontSize: 12, marginBottom: 20, lineHeight: 1.7 }}>
            To restore data (e.g. on a new device or after clearing browser data), upload a previously exported JSON backup file.
          </p>
          <label style={{
            display: 'block', padding: '14px', textAlign: 'center',
            border: `2px dashed ${BORDER}`, borderRadius: 8, cursor: 'pointer',
            color: MUTED, fontSize: 12,
          }}>
            📂 Click to select backup file
            <input type="file" accept=".json" onChange={handleImport} style={{ display: 'none' }} />
          </label>
          {imported && (
            <div style={{ marginTop: 16, padding: '14px', background: '#0F1A0F', border: `1px solid ${GREEN}30`, borderRadius: 8 }}>
              <div style={{ color: GREEN, fontSize: 12, marginBottom: 8 }}>
                Ready to import: {imported.transactions.length} transactions, {imported.invoices.length} invoices
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <PrimaryBtn onClick={confirmImport} style={{ fontSize: 11 }}>Confirm Import</PrimaryBtn>
                <GhostBtn onClick={() => setImported(null)} style={{ fontSize: 11 }}>Cancel</GhostBtn>
              </div>
            </div>
          )}
        </Card>

        {/* PASSWORD */}
        <Card style={{ padding: 28 }}>
          <div style={{ fontSize: 12, color: GOLD, letterSpacing: 1, marginBottom: 20 }}>CHANGE PASSWORD</div>
          <form onSubmit={changePassword}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <FormField label="Current Password">
                <Input type="password" value={pw} onChange={e=>setPw(e.target.value)} placeholder="Current password" />
              </FormField>
              <FormField label="New Password">
                <Input type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} placeholder="6+ characters" />
              </FormField>
              <FormField label="Confirm New Password">
                <Input type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} placeholder="Repeat new password" />
              </FormField>
              {pwMsg && (
                <div style={{ color: pwMsg.type === 'error' ? RED : GREEN, fontSize: 12 }}>
                  {pwMsg.type === 'error' ? '✕ ' : '✓ '}{pwMsg.text}
                </div>
              )}
              <PrimaryBtn type="submit">Update Password</PrimaryBtn>
            </div>
          </form>
        </Card>

        {/* DATA STATS */}
        <Card style={{ padding: 28 }}>
          <div style={{ fontSize: 12, color: GOLD, letterSpacing: 1, marginBottom: 20 }}>DATA OVERVIEW</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { label: 'Total Transactions', value: transactions.length },
              { label: 'Total Invoices', value: invoices.length },
              { label: 'Date Range', value: transactions.length > 0 ? `${transactions[transactions.length-1]?.date} → ${transactions[0]?.date}` : 'No data' },
              { label: 'Storage Used', value: (() => { try { const u = JSON.stringify(localStorage).length; return `${(u/1024).toFixed(1)} KB`; } catch { return '—'; } })() },
            ].map((stat) => (
              <div key={stat.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${BORDER}` }}>
                <span style={{ color: MUTED, fontSize: 12 }}>{stat.label}</span>
                <span style={{ color: TEXT, fontSize: 12, fontWeight: 500 }}>{stat.value}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 20 }}>
            <GhostBtn onClick={() => {
              if (window.confirm('Delete ALL transactions and invoices? This cannot be undone.')) {
                saveLS(LS_KEYS.transactions, []);
                saveLS(LS_KEYS.invoices, []);
                window.location.reload();
              }
            }} style={{ width: '100%', color: RED, borderColor: RED }}>
              ⚠ Clear All Data
            </GhostBtn>
          </div>
        </Card>
      </div>
    </div>
  );
}
